"use client"

import { useTranslations } from "next-intl"

import { formatPrice } from "@/lib/helper"
import { getCountry } from "@/lib/locale/locale-common-utils"
import { CardPrices, generateBasePriceInfo, generatePackagingInfoFromTranslations } from "@/lib/price-utils"
import { ReducedProductData } from "@/lib/storefront/product/product"
import { cn } from "@/components/ui/helpers/utils"

export default function ProductCardPrice(props: {
  productData: ReducedProductData
  cardPrices: CardPrices
  showEcoTaxFlag: boolean | undefined
  isB2BCustomer: boolean
  showPrice: boolean
  priceLoading?: boolean
  isoLocale: string
  isListView?: boolean
}) {
  const t = useTranslations("storefront")

  const cardPrices = props.cardPrices

  const ecoTax: number | undefined = props.productData?.ecoTax
  const ecoTaxFormatted = formatPrice(props.isoLocale, ecoTax, props.productData.currency)

  const showEcoTax = !!props.showEcoTaxFlag && getCountry(props.isoLocale) === "fr" && !!ecoTax && ecoTax > 0
  const priceText = props.isB2BCustomer ? t("product.price_net") : t("product.price_gross")
  const ecoTaxText = priceText + t("product.eco_tax", { ecoTax: ecoTaxFormatted })

  const packagingUnitInfo = generatePackagingInfoFromTranslations(t, props.productData, true)
  const pricePerUnitText = generateBasePriceInfo(props.isoLocale, props.productData, cardPrices.salePrice)

  return (
    <div className={"flex flex-col items-end justify-end"}>
      {props.priceLoading ? (
        <div className="flex min-h-16 w-full flex-col items-end justify-end gap-2" aria-hidden="true">
          <div className={cn("h-3 w-20 animate-pulse rounded-sm bg-gray-200", { "@xl:w-28": props.isListView })} />
          <div className={cn("h-6 w-28 animate-pulse rounded-sm bg-gray-200", { "@xl:h-8 @xl:w-36": props.isListView })} />
        </div>
      ) : props.showPrice ? (
        <>
          {showEcoTax && (
            <p className={cn("text-xs text-nowrap text-gray-600", { "@xl:text-sm @xl:font-light": props.isListView })}>
              {ecoTaxText}
            </p>
          )}

          {pricePerUnitText !== "" && (
            <p
              className={cn("text-xs text-nowrap text-gray-600", { "@xl:text-sm @xl:font-light": props.isListView })}
              dangerouslySetInnerHTML={{ __html: pricePerUnitText }}
            />
          )}
          {packagingUnitInfo !== "" && (
            <p
              className={cn("text-xs text-nowrap text-gray-600", { "@xl:text-sm @xl:font-light": props.isListView })}
              dangerouslySetInnerHTML={{ __html: packagingUnitInfo }}
            />
          )}

          {cardPrices.listPriceFormatted !== cardPrices.salePriceFormatted && cardPrices.discountPercent > 0 ? (
            <>
              {props.isB2BCustomer ? (
                <div className={cn("flex flex-row items-baseline justify-end gap-1")}>
                  {!cardPrices.isSinglePrice && (
                    <p className={cn("text-sm text-nowrap text-gray-600", { "@xl:hidden": props.isListView })}>
                      {t("product.price_from")}
                    </p>
                  )}
                  {cardPrices.listPriceFormatted !== cardPrices.salePriceFormatted && (
                    <p
                      className={cn("text-sm font-bold text-gray-600 line-through", {
                        "@xl:font-semibold @xl:text-gray-800": props.isListView,
                      })}
                    >
                      {props.isListView && (
                        <span className={"hidden @xl:inline"}>{t("product.list_price") + ": "}</span>
                      )}
                      {props.isListView && !cardPrices.isSinglePrice && (
                        <span className={"hidden @xl:inline"}>{t("product.price_from") + " "}</span>
                      )}
                      {cardPrices.listPriceFormatted}
                    </p>
                  )}
                </div>
              ) : (
                <p
                  className={cn("text-xs font-bold text-nowrap text-primary", {
                    "@xl:text-base": props.isListView,
                  })}
                >
                  {t("basket.add_dialog.special_offer")}
                </p>
              )}
            </>
          ) : (
            props.isB2BCustomer && (
              <div className={"flex flex-row items-baseline justify-end gap-1"}>
                <p
                  className={cn("text-sm font-bold text-transparent line-through", {
                    "@xl:text-base": props.isListView,
                  })}
                >
                  -
                </p>
              </div>
            )
          )}

          <div className="flex flex-row items-baseline justify-end gap-1">
            {!cardPrices.isSinglePrice && (
              <p
                className={cn("text-sm text-nowrap text-gray-600", {
                  "@xl:text-xl @xl:font-bold @xl:text-primary": props.isListView,
                })}
              >
                {t("product.price_from")}
              </p>
            )}
            <p className={cn("text-xl font-bold text-primary", { "@xl:text-3xl": props.isListView })}>
              {cardPrices.salePriceFormatted}
              {/*{isB2BCustomer ? "*" : ""}*/}
            </p>
          </div>
          {props.isB2BCustomer && props.isListView && (
            <p className={"hidden text-sm font-light text-gray-600 @xl:block"}>{t("product.tax_hint_short")}</p>
          )}
        </>
      ) : (
        <>
          {" "}
          <p className={cn("text-base font-bold text-nowrap text-primary", { "@xl:text-xl": props.isListView })}>
            {t("product.ask_price")}
          </p>
        </>
      )}
      {/*
      <textarea className={"hidden"} defaultValue={JSON.stringify(cardPrices)}/>
*/}
    </div>
  )
}
